/**
 * ProductHero.tsx — hero for produktside (ibm.com/products)
 *
 * Grått bånd, brødsmulesti, H1 (Plex Light ~54px) øverst, ingress (20/28) og
 * knapper nederst til venstre, isometrisk illustrasjon til høyre. Primærknapp
 * har pil høyre; knapper som scroller på siden har pil ned.
 */

import type { ReactNode } from 'react';
import Link from 'next/link';
import LandingButton from '@/components/landing/LandingButton';
import type { ProductBreadcrumb, ProductHeroContent } from '@/types/product';
import styles from './product.module.css';

interface ProductHeroProps {
  hero: ProductHeroContent;
  breadcrumb: ProductBreadcrumb;
  /** Illustrasjon til høyre (SVG-komponent) */
  illustration: ReactNode;
  /** Valgfritt merke etter overskriften, f.eks. «Alpha» */
  badge?: string;
  /** Valgfritt innhold mellom ingress og knapper (f.eks. domenesøk) */
  extra?: ReactNode;
}

export default function ProductHero({ hero, breadcrumb, illustration, badge, extra }: ProductHeroProps) {
  return (
    <section className={styles.hero} aria-labelledby="hero-heading">
      <div className={`${styles.container} ${styles.heroInner}`}>
        <div className={styles.heroText}>
          <div>
            <nav aria-label={breadcrumb.label}>
              <ol className={styles.crumbs}>
                {breadcrumb.links.map((link) => (
                  <li key={link.href}>
                    <Link href={link.href} className={styles.crumbLink}>
                      {link.label}
                    </Link>
                  </li>
                ))}
                <li aria-current="page">{breadcrumb.current}</li>
              </ol>
            </nav>
            <h1 id="hero-heading" className={styles.h1}>
              {hero.heading}
              {badge && <span className={styles.heroBadge}>{badge}</span>}
            </h1>
          </div>

          <div className={styles.heroBottom}>
            <p className={styles.heroLead}>{hero.subheading}</p>
            {extra}
            <ul className={styles.heroActions} role="list">
              <li>
                <LandingButton link={hero.primaryCta} kind="primary" />
              </li>
              <li>
                <LandingButton link={hero.secondaryCta} kind="tertiary" icon="down" />
              </li>
            </ul>
          </div>
        </div>

        <div className={styles.heroArt}>
          {illustration}
        </div>
      </div>
    </section>
  );
}
